import { useTranslation } from "react-i18next";
import { useAuth } from "@/src/contexts/auth.context";
import { Post } from "@/src/models/post.model";
import { useGroupNamesByIds } from "@/src/services/api/groups/groups.hooks";
import {
  formatGroupAudience,
  postGroupIds,
  VISIBILITY_LABEL_KEYS,
} from "@/src/utils/post-visibility.utils";

/**
 * The text shown beside a post's timestamp: "Public", "Only-me", or — for a
 * group post — the group's actual name rather than the word "Group".
 *
 * Names are resolved from the viewer's own group memberships, which is one
 * shared query for the whole feed. That set is sufficient by construction: the
 * feed only ever shows a group post to a member of that group, so any group post
 * on screen belongs to a group in this list. Anything that does not resolve
 * falls back to the generic label.
 */
export const usePostVisibilityLabel = (
  post?: Pick<Post, "visibility" | "groupId" | "visibleToGroupIds"> | null,
): string | null => {
  const { t } = useTranslation();
  const { userGroups } = useAuth();
  const { data: groupNames } = useGroupNamesByIds(userGroups ?? []);

  if (!post?.visibility) return null;

  const genericKey = VISIBILITY_LABEL_KEYS[post.visibility];
  const generic = genericKey ? t(genericKey) : null;

  if (post.visibility !== "group") return generic;

  const names = postGroupIds(post).map((id) => groupNames?.[id] ?? "");
  return formatGroupAudience(names) ?? generic;
};
